import { TicketingFormCategory } from "@simplyk/common";

import { Translate } from "../../../../../common/hooks/useTranslate";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

interface RatesDefaultInformations {
  rate1Title: string;
  rate2Title?: string;
  rate1Description?: string;
  rate2Description?: string;
}

interface GetRatesDefaultInformationsParams {
  formCategory: TicketingFormCategory;
  t: Translate;
  toOmit?: "title2";
}

export const getRatesDefaultInformations = ({
  formCategory,
  t,
  toOmit,
}: GetRatesDefaultInformationsParams): RatesDefaultInformations => {
  const getTitle1 = () => {
    if (isSweepstakesCategory(formCategory)) {
      return t("dashboard", "sweepstakes.rates.defaultTitle");
    }

    switch (formCategory) {
      case TicketingFormCategory.Event:
        return t("dashboard", "editor.rate1.placeholder.title");
      case TicketingFormCategory.Shop:
        return t("dashboard", "editor.rate1.placeholder.title.shop");
      case TicketingFormCategory.Lottery:
        return t("dashboard", "editor.rate1.placeholder.title.lottery");
      case TicketingFormCategory.Auction:
        return t("dashboard", "editor.rate1.placeholder.title.auction");
      case TicketingFormCategory.MembershipV2:
        return t("dashboard", "editor.rate1.placeholder.title.membership");
      case TicketingFormCategory.Custom:
        return "";
      default:
        return t("dashboard", "editor.rate1.placeholder.title");
    }
  };

  const getTitle2 = () => {
    switch (formCategory) {
      case TicketingFormCategory.Event:
      case TicketingFormCategory.Custom:
        return t("dashboard", "editor.rate2.placeholder.title");
      case TicketingFormCategory.Shop:
        return t("dashboard", "editor.rate2.placeholder.title.shop");
      case TicketingFormCategory.Lottery:
        return t("dashboard", "editor.rate2.placeholder.title.lottery");
      case TicketingFormCategory.Sweepstakes:
      case TicketingFormCategory.Auction:
      case TicketingFormCategory.MembershipV2:
        return "";
      default:
        return t("dashboard", "editor.rate2.placeholder.title");
    }
  };

  const getDescription1 = () => {
    switch (formCategory) {
      case TicketingFormCategory.Event:
        return t("dashboard", "editor.rate1.placeholder.description");
      case TicketingFormCategory.Shop:
      case TicketingFormCategory.Lottery:
      case TicketingFormCategory.Sweepstakes:
      case TicketingFormCategory.Auction:
      case TicketingFormCategory.MembershipV2:
      case TicketingFormCategory.Custom:
        return "";
      default:
        return t("dashboard", "editor.rate1.placeholder.description");
    }
  };

  const getDescription2 = () => {
    switch (formCategory) {
      case TicketingFormCategory.Event:
        return t("dashboard", "editor.rate2.placeholder.description");
      case TicketingFormCategory.Shop:
      case TicketingFormCategory.Lottery:
      case TicketingFormCategory.Sweepstakes:
      case TicketingFormCategory.Auction:
      case TicketingFormCategory.MembershipV2:
        return "";
      default:
        return t("dashboard", "editor.rate2.placeholder.description");
    }
  };

  return {
    rate1Title: getTitle1(),
    rate2Title: toOmit === "title2" ? "" : getTitle2(),
    rate1Description: getDescription1(),
    rate2Description: getDescription2(),
  };
};

export const getStepTranslations = (
  category: TicketingFormCategory,
  t: Translate
): { title: string; description: string | undefined } => {
  if (isSweepstakesCategory(category)) {
    return {
      title: t("dashboard", "sweepstakes.rates.title"),
      description: t("dashboard", "sweepstakes.rates.description"),
    };
  }

  switch (category) {
    case TicketingFormCategory.Shop:
      return {
        title: t("dashboard", "ticketing.setTicketsList.shop.title"),
        description: t("dashboard", "ticketing.setTicketsList.shop.description"),
      };
    case TicketingFormCategory.Lottery:
      return {
        title: t("dashboard", "ticketing.setTicketsList.title"),
        description: t("dashboard", "ticketing.setTicketsList.description.lottery"),
      };
    case TicketingFormCategory.Auction:
      return {
        title: t("dashboard", "ticketing.setTicketsList.auction.title"),
        description: undefined,
      };
    case TicketingFormCategory.MembershipV2:
      return {
        title: t("dashboard", "ticketing.setTicketsList.membership.title"),
        description: t("dashboard", "ticketing.setTicketsList.description.membership"),
      };
    case TicketingFormCategory.Custom:
      return {
        title: t("dashboard", "ticketing.setTicketsList.custom.title"),
        description: t("dashboard", "ticketing.setTicketsList.custom.description"),
      };
    default:
      return {
        title: t("dashboard", "ticketing.setTicketsList.title"),
        description: t("dashboard", "ticketing.setTicketsList.description"),
      };
  }
};
